import React, { useEffect, useRef, useState } from 'react';
import MessageList from 'utils/messageList';
import apiError from 'utils/conversion/api.error.conversion';
import Service from 'services';
import DeviceData from 'drivers/deviceData';
import Route from 'enum/route.enum';
import { useNavigate } from 'react-router-dom';
import { translate as t } from 'utils/i18n';
import ErrorDisplay from 'components/common/error/ErrorDisplay';

const MESSAGE = {
  msgType: MessageList.serverErrorMsg,
  msgTitle: t('errors.deviceNotFoundError'),
};

const isThreeKey = (e) =>
  e?.key === '3' || e?.code === 'Digit3' || e?.keyCode === 51 || e?.which === 51;

const DeviceRegistrationError = () => {
  const [message, setMessage] = useState(MESSAGE);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const intervalRef = useRef(null);
  const inFlightRef = useRef(false);
  const latestStableTitleRef = useRef(MESSAGE.msgTitle);
  const revertTimerRef = useRef(null);
  const keyPressCountRef = useRef(0);
  const lastKeyPressAtRef = useRef(0);

  const stopPolling = React.useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  const setStableMessageTitle = React.useCallback((title) => {
    latestStableTitleRef.current = title;
    setMessage({ msgType: MessageList.serverErrorMsg, msgTitle: title });
  }, []);

  const showTemporaryErrorTitle = React.useCallback((title, ms = 5000) => {
    if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
    setMessage({ msgType: MessageList.serverErrorMsg, msgTitle: title });
    revertTimerRef.current = setTimeout(() => {
      setMessage({
        msgType: MessageList.serverErrorMsg,
        msgTitle: latestStableTitleRef.current,
      });
    }, ms);
  }, []);

  const setProcessingUi = React.useCallback((processing) => {
    setIsProcessing(processing);
    if (processing && revertTimerRef.current) {
      clearTimeout(revertTimerRef.current);
      revertTimerRef.current = null;
    }
  }, []);

  const checkDeviceRegistration = React.useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const getUID = await DeviceData.getUID();
      const getIP = await DeviceData.getIP();
      const res = await Service.DevicesService.getRegisterDeviceVerification(getUID, getIP);
      if (res) {
        stopPolling();
        navigate(Route.route.LOADING);
      }
    } catch (err) {
      setStableMessageTitle(apiError.apiErrorRes(err?.data) || t('errors.deviceNotFoundError'));
    } finally {
      inFlightRef.current = false;
    }
  }, [navigate, setStableMessageTitle, stopPolling]);

  const startPolling = React.useCallback(() => {
    stopPolling();
    intervalRef.current = setInterval(checkDeviceRegistration, 60000);
  }, [checkDeviceRegistration, stopPolling]);

  const handleKeyDown = React.useCallback(
    async (e) => {
      if (!isThreeKey(e)) return;

      const now = Date.now();
      if (now - lastKeyPressAtRef.current > 1500) keyPressCountRef.current = 0;
      lastKeyPressAtRef.current = now;
      keyPressCountRef.current += 1;

      if (keyPressCountRef.current < 4) return;
      keyPressCountRef.current = 0;

      stopPolling();
      setProcessingUi(true);

      try {
        const serialNumber = await DeviceData.getUID();
        const ipAddress = await DeviceData.getIP();
        const res =
          await Service.DevicesService.putRegisterDeviceVerificationBySerialNumber(serialNumber, ipAddress);
        if (res?.status >= 200 && res?.status < 300) {
          navigate(Route.route.LOADING);
          return;
        }
        showTemporaryErrorTitle(t('errors.wentWrong'));
      } catch (err) {
        showTemporaryErrorTitle(apiError.apiErrorRes(err?.data) || t('errors.wentWrong'));
        await checkDeviceRegistration();
        startPolling();
      } finally {
        setProcessingUi(false);
      }
    },
    [checkDeviceRegistration, navigate, setProcessingUi, showTemporaryErrorTitle, startPolling, stopPolling]
  );

  useEffect(() => {
    checkDeviceRegistration();
    startPolling();
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      stopPolling();
      if (revertTimerRef.current) clearTimeout(revertTimerRef.current);
    };
  }, [checkDeviceRegistration, handleKeyDown, startPolling, stopPolling]);
  return (
    <ErrorDisplay
      message={isProcessing ? 'Processing…' : message.msgTitle}
      title=''
      icon={isProcessing ? '⏳' : '⚠️'}
      overlay={true}
    />
  );
};

export default DeviceRegistrationError;
